<template>
  <div class="p-4">
    <!-- Header -->
    <div class="mb-4 flex justify-between items-center">
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">Pedidos</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400">Gestiona pedidos de clientes</p>
      </div>
      <button
        @click="$router.push('/pedidos/nuevo')"
        class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors flex items-center gap-2 text-sm"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        Nuevo Pedido
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <div class="flex flex-wrap gap-4">
        <div class="flex-1 min-w-64">
          <input
            v-model="filters.search"
            @input="debouncedSearch"
            type="text"
            placeholder="Buscar por número o cliente..."
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>
        <div>
          <select
            v-model="filters.estado"
            @change="fetchPedidos(1)"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="borrador">Borradores</option>
            <option value="confirmado">Confirmados</option>
            <option value="en_produccion">En Producción</option>
            <option value="completado">Completados</option>
            <option value="entregado">Entregados</option>
            <option value="cancelado">Cancelados</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tabla de pedidos -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-4">
        <div v-if="error" class="text-red-500 text-center mb-3 text-sm">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-3 py-2 font-bold">Número</th>
                <th class="px-3 py-2 font-bold">Cotización</th>
                <th class="px-3 py-2 font-bold">Cliente</th>
                <th class="px-3 py-2 font-bold">Estado</th>
                <th class="px-3 py-2 font-bold">Items</th>
                <th class="px-3 py-2 font-bold">Total</th>
                <th class="px-3 py-2 font-bold">Fecha Pedido</th>
                <th class="px-3 py-2 font-bold">Entrega Est.</th>
                <th class="px-3 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pedido in pedidos" :key="pedido.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700">
                <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">{{ pedido.numero_pedido }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">
                  {{ pedido.cotizacion?.numero_cotizacion || 'N/A' }}
                </td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">
                  {{ getContactoNombre(pedido.contacto) }}
                </td>
                <td class="px-3 py-2">
                  <span :class="getEstadoBadgeClass(pedido.estado)" class="px-2 py-0.5 rounded-full text-xs font-medium">
                    {{ getEstadoText(pedido.estado) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                    {{ pedido.items_count || 0 }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="text-green-600 dark:text-green-400 font-medium">
                    ${{ parseFloat(pedido.total || 0).toFixed(2) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">
                  {{ formatDate(pedido.fecha_pedido) }}
                </td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">
                  {{ formatDate(pedido.fecha_entrega_estimada) }}
                </td>
                <td class="px-3 py-2 whitespace-nowrap text-right">
                  <button @click="viewPedido(pedido)" class="text-blue-500 hover:text-blue-700 mr-1" title="Ver detalles">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button
                    v-if="pedido.estado !== 'cancelado' && pedido.estado !== 'entregado'"
                    @click="cambiarEstado(pedido)"
                    class="text-green-500 hover:text-green-700 mr-1"
                    title="Cambiar estado"
                  >
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </button>
                  <button
                    v-if="pedido.estado === 'borrador'"
                    @click="onDeletePedido(pedido)"
                    class="text-red-500 hover:text-red-700"
                    title="Eliminar"
                  >
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="pedidos.length === 0">
                <td colspan="9" class="text-center py-6 text-gray-400 text-sm">
                  {{ loading ? 'Cargando...' : 'No hay pedidos para mostrar.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Paginación -->
      <Pagination
        v-if="pagination && pagination.total > 0"
        :current-page="pagination.current_page"
        :total-pages="pagination.last_page"
        :total="pagination.total"
        :per-page="pagination.per_page"
        @page-changed="fetchPedidos"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import Pagination from '@/components/Pagination.vue'
import Swal from 'sweetalert2'

const router = useRouter()
const { get, del, post, loading, error, data } = useHttp()
const { showSuccess, showError } = useNotifications()

// Estado
const pedidos = ref([])
const pagination = ref(null)
const filters = ref({
  search: '',
  estado: ''
})

// Cargar pedidos
const fetchPedidos = async (page = 1) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: '15'
    })

    if (filters.value.search) {
      params.append('numero_pedido', filters.value.search)
      params.append('contacto_nombre', filters.value.search)
    }

    if (filters.value.estado) {
      params.append('estado', filters.value.estado)
    }

    await get(`pedidos?${params.toString()}`)
    if (data.value) {
      pedidos.value = data.value.data || data.value
      pagination.value = data.value.meta || data.value
    }
  } catch (err) {
    showError('Error al cargar pedidos')
  }
}

// Búsqueda con debounce
const debouncedSearch = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      fetchPedidos(1)
    }, 500)
  }
})()

// Acciones
const viewPedido = (pedido) => {
  router.push(`/pedidos/${pedido.id}`)
}

const cambiarEstado = async (pedido) => {
  const estados = [
    { value: 'borrador', label: 'Borrador' },
    { value: 'confirmado', label: 'Confirmado' },
    { value: 'en_produccion', label: 'En Producción' },
    { value: 'completado', label: 'Completado' },
    { value: 'entregado', label: 'Entregado' },
    { value: 'cancelado', label: 'Cancelado' }
  ]

  const { value: nuevoEstado } = await Swal.fire({
    title: 'Cambiar Estado del Pedido',
    text: `Pedido: ${pedido.numero_pedido}`,
    input: 'select',
    inputOptions: Object.fromEntries(estados.map(e => [e.value, e.label])),
    inputValue: pedido.estado,
    showCancelButton: true,
    confirmButtonColor: '#8b5cf6',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Cambiar',
    cancelButtonText: 'Cancelar',
    inputValidator: (value) => {
      if (!value) {
        return 'Debes seleccionar un estado'
      }
    }
  })

  if (nuevoEstado && nuevoEstado !== pedido.estado) {
    try {
      await post(`pedidos/${pedido.id}/cambiar-estado`, { estado: nuevoEstado })
      await fetchPedidos()
      showSuccess('Estado actualizado exitosamente')
    } catch (err) {
      showError('No se pudo cambiar el estado')
    }
  }
}

const onDeletePedido = async (pedido) => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Eliminar pedido?',
    text: `¿Estás seguro de que deseas eliminar "${pedido.numero_pedido}"? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await del(`pedidos/${pedido.id}`)
      await fetchPedidos()
      showSuccess('Pedido eliminado exitosamente')
    } catch (err) {
      showError('No se pudo eliminar el pedido')
    }
  }
}

// Utilidades
const getContactoNombre = (contacto) => {
  if (!contacto) return 'Sin asignar'
  return contacto.razon_social || `${contacto.nombre || ''} ${contacto.apellido_paterno || ''}`.trim() || 'Sin nombre'
}

const getEstadoBadgeClass = (estado) => {
  const classes = {
    'borrador': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    'confirmado': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'en_produccion': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    'completado': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'entregado': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    'cancelado': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
  }
  return classes[estado] || classes.borrador
}

const getEstadoText = (estado) => {
  const texts = {
    'borrador': 'Borrador',
    'confirmado': 'Confirmado',
    'en_produccion': 'En Producción',
    'completado': 'Completado',
    'entregado': 'Entregado',
    'cancelado': 'Cancelado'
  }
  return texts[estado] || 'Borrador'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

onMounted(() => {
  fetchPedidos()
})
</script>
